import { useState, useMemo, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, MagnifyingGlassIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/outline';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import {
  createInventoryItemAsync,
  updateInventoryItemAsync,
  deleteInventoryItemAsync,
  createTransactionAsync,
} from '../store/keyInventorySlice';
import { fetchAssignments } from '../store/assignmentsSlice';
import { fetchKeyTypes } from '../store/keyTypesSlice';
import { KeyInventoryItem } from '../types';
import Card from '../components/Card';
import Button from '../components/Button';
import Modal from '../components/Modal';

interface InventoryFormData {
  keyTypeId: string;
  quantity: number;
  notes: string;
}

export default function KeyInventory() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { inventory, loading } = useAppSelector((state) => state.keyInventory);
  const { assignments } = useAppSelector((state) => state.assignments);
  const locations = useAppSelector((state) => state.locations.locations);
  const currentLocationId = useAppSelector((state) => state.locations.currentLocationId);
  const keyTypes = useAppSelector((state) => state.keyTypes.keyTypes);
  const currentUser = useAppSelector((state) => state.users.currentUser);

  // Get the current location object
  const currentLocation = useMemo(() => {
    return locations.find(loc => loc.id === currentLocationId);
  }, [locations, currentLocationId]);

  // Calculate assigned quantities per inventory item
  const assignedByInventoryItem = useMemo(() => {
    const assigned = new Map<string, number>();
    assignments
      .filter(a => a.status === 'active')
      .forEach(a => {
        const current = assigned.get(a.keyInventoryItemId) || 0;
        assigned.set(a.keyInventoryItemId, current + a.quantity);
      });
    return assigned;
  }, [assignments]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<KeyInventoryItem | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAllLocations, setShowAllLocations] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const { register, handleSubmit, reset, setValue } = useForm<InventoryFormData>();

  // Ensure keyTypes and assignments are loaded
  useEffect(() => {
    if (keyTypes.length === 0) {
      dispatch(fetchKeyTypes());
    }
    dispatch(fetchAssignments());
  }, [keyTypes.length, dispatch]);

  const filteredInventory = useMemo(() => {
    return inventory.filter((item) => {
      const keyType = keyTypes.find((kt) => kt.id === item.keyTypeId);

      const matchesSearch = keyType?.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesLocation = showAllLocations || item.locationId === currentLocationId;

      return matchesSearch && matchesLocation;
    });
  }, [inventory, keyTypes, searchTerm, showAllLocations, currentLocationId]);

  const handleOpenModal = (item?: KeyInventoryItem) => {
    if (item) {
      setEditingItem(item);
      setValue('keyTypeId', item.keyTypeId);
      setValue('quantity', item.quantity);
      setValue('notes', item.notes || '');
    } else {
      setEditingItem(null);
      reset();
      setValue('quantity', 1); // Default quantity to 1
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
    reset();
  };

  const onSubmit = async (data: InventoryFormData) => {
    if (!currentUser || !currentLocationId) return;

    setIsSaving(true);
    const timestamp = new Date().toISOString();

    try {
      if (editingItem) {
        const quantityDiff = data.quantity - editingItem.quantity;
        const updatedItem: KeyInventoryItem = {
          ...editingItem,
          keyTypeId: data.keyTypeId,
          quantity: data.quantity,
          notes: data.notes,
          lastUpdated: timestamp,
          updatedBy: currentUser.id,
        };
        await dispatch(updateInventoryItemAsync(updatedItem)).unwrap();

        if (quantityDiff !== 0) {
          await dispatch(createTransactionAsync({
            id: `txn-${Date.now()}`,
            keyInventoryItemId: editingItem.id,
            type: 'adjustment',
            quantity: quantityDiff,
            userId: currentUser.id,
            timestamp,
            notes: data.notes,
          })).unwrap();
        }
      } else {
        // Check if inventory already exists for this key type and current location
        const existingItem = inventory.find(
          (item) => item.keyTypeId === data.keyTypeId && item.locationId === currentLocationId
        );

        if (existingItem) {
          // Add to existing inventory
          const updatedItem: KeyInventoryItem = {
            ...existingItem,
            quantity: existingItem.quantity + data.quantity,
            notes: data.notes || existingItem.notes,
            lastUpdated: timestamp,
            updatedBy: currentUser.id,
          };
          await dispatch(updateInventoryItemAsync(updatedItem)).unwrap();
          await dispatch(createTransactionAsync({
            id: `txn-${Date.now()}`,
            keyInventoryItemId: existingItem.id,
            type: 'add',
            quantity: data.quantity,
            userId: currentUser.id,
            timestamp,
            notes: data.notes,
          })).unwrap();
        } else {
          // Create new inventory item at current location
          const newItem: KeyInventoryItem = {
            id: `inv-${Date.now()}`,
            keyTypeId: data.keyTypeId,
            locationId: currentLocationId,
            quantity: data.quantity,
            notes: data.notes,
            lastUpdated: timestamp,
            updatedBy: currentUser.id,
          };
          await dispatch(createInventoryItemAsync(newItem)).unwrap();
          await dispatch(createTransactionAsync({
            id: `txn-${Date.now()}`,
            keyInventoryItemId: newItem.id,
            type: 'add',
            quantity: data.quantity,
            userId: currentUser.id,
            timestamp,
            notes: data.notes,
          })).unwrap();
        }
      }

      handleCloseModal();
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      console.error('Failed to save inventory item:', errorMsg);
      alert(`Failed to save: ${errorMsg}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (item: KeyInventoryItem) => {
    if (!currentUser) return;

    const keyTypeName = keyTypes.find(kt => kt.id === item.keyTypeId)?.name || 'this key';
    const assignedCount = assignedByInventoryItem.get(item.id) || 0;

    if (assignedCount > 0) {
      alert(`Cannot delete ${keyTypeName}. There are ${assignedCount} key(s) currently assigned. Please return all keys before deleting this inventory item.`);
      return;
    }

    if (confirm(`Are you sure you want to remove ${keyTypeName}?`)) {
      try {
        await dispatch(createTransactionAsync({
          id: `txn-${Date.now()}`,
          keyInventoryItemId: item.id,
          type: 'remove',
          quantity: -item.quantity,
          userId: currentUser.id,
          timestamp: new Date().toISOString(),
          notes: 'Item removed from inventory',
        })).unwrap();
        await dispatch(deleteInventoryItemAsync(item.id)).unwrap();
      } catch (error) {
        console.error('Failed to delete inventory item:', error);
        alert('Failed to delete. Please try again.');
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-secondary-100">Key Inventory</h1>
          <p className="mt-1 text-sm text-secondary-600 dark:text-secondary-400">
            Manage your physical key inventory
          </p>
        </div>
        <Button onClick={() => handleOpenModal()} disabled={loading}>
          <PlusIcon className="w-5 h-5 mr-2" />
          Add Key
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <div className="flex flex-col gap-4 md:flex-row md:items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
              Search Keys
            </label>
            <div className="relative">
              <MagnifyingGlassIcon className="absolute w-5 h-5 transform -translate-y-1/2 left-3 top-1/2 text-secondary-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by key name..."
                className="w-full py-2 pl-10 pr-4 border rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showAllLocations}
                onChange={(e) => setShowAllLocations(e.target.checked)}
                className="w-4 h-4 text-primary-600 border-secondary-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-secondary-700 dark:text-secondary-300">
                Show all locations
              </span>
            </label>
          </div>
        </div>
      </Card>

      {/* Inventory Table */}
      <Card>
        {loading && (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
            <span className="ml-2 text-secondary-600 dark:text-secondary-400">Loading...</span>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-secondary-200 dark:border-secondary-700">
                <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                  Key Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                  Location
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                  Available / Total
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                  Last Updated
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-secondary-200 dark:divide-secondary-700">
              {filteredInventory.length > 0 ? (
                filteredInventory.map((item) => {
                  const keyType = keyTypes.find((kt) => kt.id === item.keyTypeId);
                  const location = locations.find((loc) => loc.id === item.locationId);
                  const assigned = assignedByInventoryItem.get(item.id) || 0;
                  const available = item.quantity - assigned;

                  return (
                    <tr key={item.id} className="hover:bg-secondary-50 dark:hover:bg-secondary-700/50">
                      <td className="px-4 py-3 text-sm font-medium">
                        <button
                          onClick={() => keyType && navigate(`/inventory/key-type/${keyType.id}`)}
                          className="text-secondary-900 dark:text-secondary-100 hover:text-primary-600 dark:hover:text-primary-400 hover:underline text-left"
                        >
                          {keyType?.name}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-sm text-secondary-600 dark:text-secondary-400">
                        {location?.name}
                      </td>
                      <td className="px-4 py-3 text-sm text-secondary-600 dark:text-secondary-400">
                        <span className={available <= 0 ? 'text-red-600 dark:text-red-400 font-medium' : ''}>
                          {available}
                        </span>
                        <span className="text-secondary-400 dark:text-secondary-500"> / {item.quantity}</span>
                        {assigned > 0 && (
                          <span className="ml-2 text-xs text-secondary-400 dark:text-secondary-500">
                            ({assigned} assigned)
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-secondary-600 dark:text-secondary-400">
                        {new Date(item.lastUpdated).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleOpenModal(item)}
                            className="p-1 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded"
                            disabled={loading}
                          >
                            <PencilIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(item)}
                            className="p-1 text-danger hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                            disabled={loading}
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-secondary-500 dark:text-secondary-400">
                    No inventory items found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingItem ? 'Edit Key Inventory' : 'Add Key Inventory'}
        footer={
          <>
            <Button variant="secondary" onClick={handleCloseModal} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleSubmit(onSubmit)} disabled={isSaving || !currentLocationId}>
              {isSaving ? 'Saving...' : editingItem ? 'Update' : 'Add'}
            </Button>
          </>
        }
      >
        <form className="space-y-4">
          {/* Show current location (read-only) */}
          <div className="p-3 bg-secondary-50 dark:bg-secondary-700/50 rounded-lg">
            <div className="text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wide mb-1">
              Location
            </div>
            <div className="text-sm font-medium text-secondary-900 dark:text-secondary-100">
              {editingItem 
                ? locations.find(l => l.id === editingItem.locationId)?.name || 'Unknown'
                : currentLocation?.name || 'No location selected'}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
              Key Type *
            </label>
            <select
              {...register('keyTypeId', { required: true })}
              className="w-full px-4 py-2 border rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              disabled={isSaving}
            >
              <option value="">Select a key type</option>
              {keyTypes.map((keyType) => (
                <option key={keyType.id} value={keyType.id}>
                  {keyType.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
              Quantity *
            </label>
            <input
              type="number"
              min="0"
              {...register('quantity', { required: true, valueAsNumber: true })}
              className="w-full px-4 py-2 border rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              disabled={isSaving}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
              Notes
            </label>
            <textarea
              {...register('notes')}
              rows={3}
              className="w-full px-4 py-2 border rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              disabled={isSaving}
            />
          </div>
        </form>
      </Modal>
    </div>
  );
}
